import { GamePriceSlice } from '@/features/games/shared/types/game-store.types';
import { GamePriceDataResponse } from '@/features/games/shared/types/game-data';
import type { StateCreator } from 'zustand';
import { GameStore } from '@/features/games/shared/store/game-store';
import { TIME_CONSTANTS_IN_MS } from '@ecom/helper-utils';

const inflightPriceRequests = new Map<string, Promise<void>>();

export const createGamePriceSlice: StateCreator<GameStore, [], [], GamePriceSlice> = (
  set,
  get,
) => ({
  prices: {},
  promotions: {},
  timestamps: {},

  fetchPrices: async (gameId: string): Promise<void> => {
    const { prices, timestamps } = get();
    const now = Date.now();

    const isCached = prices[gameId] !== undefined;
    const isFresh = isCached && now - (timestamps[gameId] ?? 0) < TIME_CONSTANTS_IN_MS.TEN_MINUTES;

    if (isCached && isFresh) return;

    if (inflightPriceRequests.has(gameId)) {
      await inflightPriceRequests.get(gameId);
      return;
    }

    const fetchPromise = (async () => {
      try {
        const res = await fetch(`/api/game/${gameId}/prices`, {
          cache: 'no-store',
        });

        const rawPrices: GamePriceDataResponse = await res.json();
        if (!rawPrices.prices) {
          throw new Error('Invalid price data');
        }

        set({
          prices: { ...get().prices, [gameId]: rawPrices },
          promotions: { ...get().promotions, [gameId]: rawPrices.promotion },
          timestamps: { ...get().timestamps, [gameId]: Date.now() },
        });
      } catch (err) {
        console.error(`Failed to fetch prices for ${gameId}`, err);
      } finally {
        inflightPriceRequests.delete(gameId);
      }
    })();

    inflightPriceRequests.set(gameId, fetchPromise);
    return fetchPromise;
  },

  bustGamePrices: (gameId: string) => {
    const state = get();
    const prices = { ...state.prices };
    const timestamps = { ...state.timestamps };
    const promotions = { ...state.promotions };
    delete prices[gameId];
    delete timestamps[gameId];
    delete promotions[gameId];
    set({ prices, timestamps });
  },
});
